// Closes the endpoint-list update loop for every platform (iW9-D;
// docs/04-censorship-resistance.md §2). The engine already VERIFIES and
// persists signed endpoint-list updates (Ed25519 + keyring, see
// call/crypto/endpointList.ts + call/signaling/client.ts) — this module is the
// thing that actually FETCHES one, from the two channels §2 lists as reachable
// from a client:
//
//   origin  `GET ${apiBaseUrl}/endpoints` on whatever host REST is currently
//           routed to (the signaling race winner, not a pinned base — "not from
//           a single pinned origin, which would make the healing channel die
//           with the host it exists to route around"). Bearer-gated
//           (backend/internal/endpoints), so an authenticated caller gets the
//           FULL document.
//   mirror  a static copy on independent infrastructure (GitHub raw /
//           Cloudflare Pages / S3 — different ASNs, high collateral damage to
//           block). Unauthenticated, untrusted, and the only channel left to a
//           client that can no longer reach ANY of our endpoints.
//
// Cadence matches Android's `CHECK_INTERVAL_MS` (shell/appUpdate.ts, 6h), the
// one concrete cadence either platform uses for this poll.
//
// ORDERING AND COST — why the mirror is a fallback and not a second parallel
// fetch. Firing both on every refresh would double the request volume of the
// healthy path to buy nothing: an authenticated origin fetch returns the full
// document, which is a superset of what an unauthenticated static copy can
// serve (docs/04 §2, "serve subsets, never the fleet"), so the mirror can only
// ever repeat or lag it. It would also open a second flow, to a second host, on
// every device, every 6h — and per-host connection policing at flow SETUP is
// one of the documented blocking mechanisms (docs/04 §1, lib/http.ts). So the
// mirror is spent only when the origin leg produced NO HTTP RESPONSE AT ALL:
// the token mint or the request itself died at the transport. Any answered
// status — 200, 404 (what production serves today), 401, 5xx — proves the host
// was reachable and ends the refresh there. That single rule is what keeps
// mirror traffic at exactly zero for every user who is not blocked.
//
// The rule binds the TOKEN MINT too, and that is not a detail. `GET /endpoints`
// is Bearer-gated, so a refresh begins by asking for an access token, and a
// revoked or expired session makes `POST /auth/refresh` answer 401 — from a
// host the device demonstrably reached. Treating that as "unreachable" would
// send a device with a perfectly working link to a third-party mirror at every
// boot and every 6h, forever, for a list it already has and cannot use: the
// mirror is unauthenticated, so it cannot mend a session, and the origin
// answered, so nothing needs mending. It would also make this module disagree
// with itself — a 401 from `/endpoints` ends the refresh while a 401 from
// `/auth/refresh` continued past it. So the mint is classified by whether a
// server ANSWERED (see `answeredStatus`), exactly like the request: a status
// ends the refresh, a transport failure — which is the 2026-07-26 field
// incident, a live WSS socket with every fresh HTTPS flow dying — earns the
// mirror its one request.
//
// Adoption semantics (from call/signaling/client.ts `updateEndpointList`):
// a verified document REPLACES the in-memory racing list and persists
// immediately, but the currently-live socket is left alone — the new list
// is only consulted the next time the client races endpoints (reconnect
// after a drop, or next app launch). There is no mid-session forced
// reconnect here, matching the web/Android engine exactly.
//
// TRUST: there is exactly ONE trust path, `engine.updateEndpointList` — valid
// Ed25519 signature against the baked keyring AND a strictly higher version.
// The mirror gets no privilege of its own, which is precisely why it is safe
// for it to be unauthenticated: a hostile mirror can serve a stale document at
// worst, and any tampering fails the signature.
//
// Tolerance: production serves nothing at `/endpoints` today, so 404, any
// network failure, a malformed body, and a failed verification are ALL
// silent no-ops — info/debug log only, never `error` (CLAUDE.md: a refresh
// miss is non-fatal, the current list keeps working).

import type { EndpointList } from '@merkury/protocol';
import type { CallEngine } from '../call/engine.js';
import {
  HTTP_LONE_ATTEMPT_TIMEOUT_MS,
  httpFailureKind,
  withDeadline,
  type ResilientFetch,
} from '../lib/http.js';
import { safeHostFromUrl } from './diag/redact.js';
import { setEndpointPins } from './native/pins.js';

/**
 * Matches Android's `CHECK_INTERVAL_MS` (shell/appUpdate.ts) — the one
 * concrete web/Android cadence found for polling `${apiBaseUrl}/endpoints`.
 */
export const ENDPOINT_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * Hard ceiling on ONE mirror attempt, ours rather than the transport's.
 *
 * The mirror fetch is issued with `retry: false` so the resilient fetch gives
 * it a single lone-attempt deadline; this backstop exists for the seams that do
 * not bound themselves — the iOS native `fetch` (runtime/native/http.ts) and any
 * host-supplied one — because a mirror that accepts the connection and then
 * never answers is exactly what a blocked link looks like, and the refresh loop
 * re-arms only after an attempt SETTLES. One second wider than the lone-attempt
 * budget so a fetch that does bound itself reports its own honest failure kind
 * instead of being pre-empted here. Derived, so the two cannot drift apart.
 */
export const MIRROR_ATTEMPT_BUDGET_MS = HTTP_LONE_ATTEMPT_TIMEOUT_MS + 1_000;

/**
 * Hard ceiling on the mirror's BODY, ours rather than the transport's.
 *
 * `withDeadline` races a timer and does not abort the loser (lib/http.ts), and
 * a plain `res.json()` buffers whatever arrives — so a mirror that accepts the
 * connection and then streams slowly keeps allocating after the refresh has
 * already given up on it, once every 6h, on a device that is by hypothesis
 * being interfered with. The mirror is the one channel docs/04 §2 calls
 * untrusted, so it is the one that gets a size bound. A signed endpoint list is
 * a few kilobytes even with a large fleet; 64 KB is two orders of headroom and
 * still a bound.
 */
export const MIRROR_MAX_BODY_BYTES = 64 * 1024;

export interface EndpointRefreshDeps {
  engine: CallEngine;
  apiBaseUrl: string;
  /** Access token minter — `GET /endpoints` is Bearer-gated. */
  getToken: () => Promise<string>;
  /** Injectable for tests; defaults to the ambient (polyfilled) fetch. */
  fetchFn?: typeof fetch;
  /**
   * Where this module's lines go. Injected rather than taken from
   * `native/host.ts`, which is the JSC bridge and NOTHING else has it: the
   * Android WebView brain and the web shell run this same module with no
   * `__host` global at all, so reaching for it directly threw a TypeError out of
   * the refresh — on the failure branch, i.e. exactly when the healing channel
   * is the thing that matters. `core.ts` passes the logger that feeds the log
   * ring and `dev.logs`, so every host gets these lines where it already looks
   * for them; absent, the module is simply quiet.
   */
  log?: (level: string, msg: string) => void;
  /**
   * Independent-mirror URL for the signed document (docs/04 §2, third channel).
   * Absolute `https://` with a DNS hostname, or it is ignored — see
   * {@link mirrorTarget}. Absent (nobody has provisioned one today) means the
   * origin leg is the whole refresh, exactly as before this existed.
   */
  mirrorUrl?: string;
  /**
   * Fetch for the mirror leg. Deliberately SEPARATE from `fetchFn`, which the
   * hosts wrap in `routeFailures(origin, …)`: the mirror is not one of our
   * origins, and a mirror that is blocked must never be able to report a
   * failure against the REST origin a call is about to use. Defaults to
   * `fetchFn`, then to the ambient fetch.
   */
  mirrorFetchFn?: ResilientFetch;
}

/** What the origin leg settled as — the mirror is spent on `unreachable` only. */
type OriginOutcome = 'adopted' | 'answered' | 'unreachable';

/**
 * A failure as a KIND, never a message. The text of a transport error is
 * written by the platform — URLSession on iOS, Chromium in the WebView — and
 * can name the host it could not reach; `lib/http.ts` already classifies the
 * only distinction worth logging (slow vs refused vs frozen), and a name is all
 * the fallback takes.
 */
function failureKind(err: unknown): string {
  return httpFailureKind(err) ?? (err instanceof Error ? err.name : 'error');
}

/**
 * The HTTP status a failure says a server ANSWERED with, or undefined when
 * nothing answered. This is the transport-vs-answer discriminator for the leg
 * that has no `Response` to look at — the token mint.
 *
 * A status is the one thing only a server can produce: every error type in this
 * codebase that carries one was built FROM a response (`RestError`,
 * `AuthHttpError` in runtime/account.ts, the web shell's `ApiError`), while the
 * failures that matter here carry none — a `TypeError` from a dead fetch, and
 * this module's own `AbortError`s, which `httpFailureKind` tags instead. Read
 * structurally rather than by `instanceof` on purpose: `getToken` is injected,
 * three hosts supply it, and none of their error classes should have to be
 * imported here for the rule to hold.
 */
function answeredStatus(err: unknown): number | undefined {
  if (err === null || typeof err !== 'object') return undefined;
  if (httpFailureKind(err) !== undefined) return undefined;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' && status >= 100 && status <= 599 ? status : undefined;
}

/** Log a line without ever becoming the reason a refresh failed. */
function say(deps: EndpointRefreshDeps, level: string, msg: string): void {
  try {
    deps.log?.(level, msg);
  } catch {
    // A broken host logger must not take the healing channel down with it.
  }
}

/** A mirror we are willing to dial: the exact string to fetch, and the most
 *  this module may say about it out loud. */
export interface MirrorTarget {
  /** The URL to dial — TRIMMED, which is the string that was admitted. A
   *  build-time value arrives from a Makefile and can carry a trailing newline;
   *  browsers strip surrounding whitespace from a fetch URL, the iOS seam
   *  (runtime/native/http.ts hands the string straight to `__host.httpRequest`)
   *  does not, so validating one string and dialling another is how a
   *  configuration that passed admission fails on one platform only. */
  url: string;
  /** What a log line may call it: the hostname, or `[host]` when the name is
   *  not one diag/redact.ts is willing to print. */
  name: string;
}

/**
 * Real DNS limits (RFC 1035 §2.3.4) — the constraint on a name that has to
 * RESOLVE, which is a different question from the one `diag/redact.ts` answers.
 */
const MAX_DNS_LABEL_CHARS = 63;
const MAX_DNS_NAME_CHARS = 253;

/**
 * A dot-separated DNS name ending in an alphabetic or `xn--` TLD. Same allow-list
 * shape as `lib/http.ts` `hostOf` and `diag/redact.ts` `safeHost`, and it is what
 * excludes every spelling of a literal address: a dotted quad ends in digits,
 * IPv6 carries `:`/brackets, and the decimal/hex forms a URL parser accepts have
 * no dot at all. (`safeHost`'s regex is not exported; see the cross-boundary note
 * in this job's report — when `lib/http.ts` exports one, all three collapse.)
 */
const DIALABLE_HOST =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.(?:[a-z]{2,}|xn--[a-z0-9-]{2,})$/i;

/**
 * The mirror as something to dial, or null.
 *
 * ALLOW-list, never a deny-list (CLAUDE.md):
 *
 *   * `https:` only — a mirror is a third party on the open internet, and a
 *     cleartext copy is one a middlebox can rewrite (the signature would catch
 *     the tamper, but the fetch would still be a plaintext beacon).
 *   * no userinfo — "no credentials in URLs" is non-negotiable, and `@` in the
 *     authority is the only way one can appear here.
 *   * the authority must be a DNS name within the real DNS length limits, so a
 *     URL naming a literal address is never dialled and never logged.
 *
 * What it deliberately does NOT require is that the name be one we may PRINT.
 * `safeHostFromUrl` additionally collapses any name with a label over 24
 * characters, more than six labels, or over 64 in total — bounds that exist to
 * keep UUID-shaped hostnames out of a REPORT, not to decide what may be
 * fetched. `merkury-endpoint-mirror-prod.s3.eu-central-1.amazonaws.com` is an
 * entirely ordinary S3 mirror that trips the first of them; using a redaction
 * rule as a dialling rule would have refused it silently, with one `debug` line
 * to explain the outage. So the two questions are separated: the regex above
 * decides what is dialled, `safeHostFromUrl` decides what is said, and a mirror
 * whose name is not publishable is fetched and reported as `[host]`.
 *
 * Hand-rolled parse rather than `new URL`: the iOS brain runs in a bare
 * JSContext with no URL constructor (same reason diag/redact.ts hand-rolls it).
 */
export function mirrorTarget(raw: string | undefined): MirrorTarget | null {
  if (typeof raw !== 'string') return null;
  const url = raw.trim();
  const authority = /^https:\/\/([^/?#]+)/i.exec(url)?.[1];
  if (authority === undefined || authority.includes('@')) return null;
  const host = authority.startsWith('[') ? authority : authority.replace(/:\d+$/, '');
  if (host.length > MAX_DNS_NAME_CHARS || !DIALABLE_HOST.test(host)) return null;
  if (host.split('.').some((label) => label.length > MAX_DNS_LABEL_CHARS)) return null;
  // The NAME still comes out of the redaction allow-list — `[host]` for the
  // shapes it will not print — so the print rule cannot widen by accident here.
  return { url, name: safeHostFromUrl(url) };
}

/**
 * Fetch one signed endpoint-list document and feed it through the engine's
 * own verify-then-adopt path (signature vs the boot keyring, version must be
 * strictly newer). The origin first; the independent mirror only if the origin
 * could not be reached at all (see the ordering note at the top of this file).
 * On adoption, re-registers SPKI pins from the SAME verified document the engine
 * just accepted — never from the raw fetch body. Never throws.
 */
export async function refreshEndpoints(deps: EndpointRefreshDeps): Promise<void> {
  if ((await refreshFromOrigin(deps)) !== 'unreachable') return;
  await refreshFromMirror(deps);
}

async function refreshFromOrigin(deps: EndpointRefreshDeps): Promise<OriginOutcome> {
  const fetchFn = deps.fetchFn ?? fetch;
  let res: Awaited<ReturnType<typeof fetch>>;
  try {
    const token = await deps.getToken();
    res = await fetchFn(`${deps.apiBaseUrl}/endpoints`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    // A server ANSWERED and the answer was a failure — a revoked session's
    // 401 from `/auth/refresh` is the case that matters. The host is reachable,
    // so the list this device holds is working and the unauthenticated mirror
    // could not mend the session anyway: the refresh ends here, exactly as it
    // does for a 401 from `/endpoints` itself.
    const status = answeredStatus(err);
    if (status !== undefined) {
      say(deps, 'debug', `endpoint refresh: token mint answered ${status}`);
      return 'answered';
    }
    // Network failure / offline / a token chain that never completed: the
    // current list keeps working (prime directive — never let a refresh miss
    // threaten the live call path), and this is the ONE outcome that earns the
    // mirror a request. It is also the exact shape of the field incident: a
    // live WSS socket while every fresh HTTPS flow died at our own timeout.
    say(deps, 'debug', `endpoint refresh: origin unreachable (${failureKind(err)})`);
    return 'unreachable';
  }
  if (res.status === 404) {
    // Nothing published yet — the expected production state today. Silent.
    return 'answered';
  }
  if (!res.ok) {
    say(deps, 'debug', `endpoint refresh: server answered ${res.status}`);
    return 'answered';
  }
  let doc: unknown;
  try {
    doc = await res.json();
  } catch (err) {
    say(deps, 'debug', `endpoint refresh: malformed body (${failureKind(err)})`);
    return 'answered';
  }
  return (await adopt(deps, doc, 'origin')) ? 'adopted' : 'answered';
}

/**
 * The third distribution channel (docs/04 §2). Never throws, never touches the
 * current list except through {@link adopt}, and cannot outlive
 * {@link MIRROR_ATTEMPT_BUDGET_MS} — nothing waits on this, but the refresh
 * timer re-arms only once it settles.
 */
async function refreshFromMirror(deps: EndpointRefreshDeps): Promise<void> {
  const target = mirrorTarget(deps.mirrorUrl);
  if (target === null) {
    if (deps.mirrorUrl !== undefined) say(deps, 'debug', 'endpoint refresh: mirror url rejected');
    return;
  }
  const { url, name } = target;
  const fetchFn = deps.mirrorFetchFn ?? deps.fetchFn ?? fetch;
  let fetched: MirrorFetch;
  try {
    fetched = await withDeadline(
      fetchMirrorDocument(fetchFn, url),
      MIRROR_ATTEMPT_BUDGET_MS,
      'mirror',
    );
  } catch (err) {
    // Blocked, refused, or frozen — all the same non-event. `name` came out of
    // the allow-list above and the kind is ours, so this line cannot carry an
    // address whatever the mirror or the platform does.
    say(deps, 'debug', `endpoint refresh: mirror ${name} unreachable (${failureKind(err)})`);
    return;
  }
  if (!fetched.ok) {
    say(deps, 'debug', `endpoint refresh: mirror ${name} ${fetched.why}`);
    return;
  }
  await adopt(deps, fetched.doc, `mirror ${name}`);
}

/** Everything a mirror answer can be, said in words this module wrote itself. */
type MirrorFetch = { ok: true; doc: unknown } | { ok: false; why: string };

async function fetchMirrorDocument(fetchFn: ResilientFetch, url: string): Promise<MirrorFetch> {
  const res = await fetchFn(url, {
    // NO Authorization header and no ambient credentials: this host is a third
    // party, and the document it serves is public by design. A bearer token
    // handed to a mirror would be a session leak to whoever runs it.
    credentials: 'omit',
    cache: 'no-store',
    // One attempt. A mirror that does not answer now is retried at the next
    // refresh (6h) or the next launch; spending a second connection to the same
    // blocked host inside one attempt buys nothing and costs another policed
    // flow.
    retry: false,
  });
  if (!res.ok) return { ok: false, why: `answered ${res.status}` };
  const text = await boundedText(res, MIRROR_MAX_BODY_BYTES);
  if (text === null) return { ok: false, why: `served over ${MIRROR_MAX_BODY_BYTES} bytes` };
  try {
    return { ok: true, doc: JSON.parse(text) };
  } catch {
    // A landing page, an interception portal, a truncated body: not a document.
    return { ok: false, why: 'served a body that is not JSON' };
  }
}

/**
 * At most `cap` bytes of a response body, or null when it is longer. The read is
 * ABANDONED at the cap — the stream is cancelled, not drained and then measured
 * — so an endless body costs a bounded amount of memory rather than however much
 * the mirror felt like sending.
 *
 * Streams where the seam has one (browsers, the Android WebView, Node). The iOS
 * native fetch hands back a string the platform already buffered
 * (runtime/native/http.ts: no streaming by construction), so there the cap can
 * only be applied after the fact — that is the honest limit of that seam, not a
 * second policy. `TextDecoder` is checked for the same reason the ring hand-rolls
 * its UTF-8 length: a bare JSContext has neither until the polyfills load.
 */
async function boundedText(res: Response, cap: number): Promise<string | null> {
  const body = (res as { body?: ReadableStream<Uint8Array> | null }).body ?? null;
  if (body === null || typeof body.getReader !== 'function' || typeof TextDecoder !== 'function') {
    const text = await res.text();
    return text.length > cap ? null : text;
  }
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let seen = 0;
  let text = '';
  for (;;) {
    const chunk = await reader.read();
    if (chunk.done) break;
    const value = chunk.value;
    if (value === undefined) continue;
    seen += value.byteLength;
    if (seen > cap) {
      void reader.cancel().catch(() => undefined);
      return null;
    }
    text += decoder.decode(value, { stream: true });
  }
  return text + decoder.decode();
}

/**
 * The one and only trust path. `engine.updateEndpointList` verifies the Ed25519
 * signature against the baked keyring and requires a STRICTLY higher version;
 * anything else leaves the current list, its persistence and its pins exactly
 * as they were. Reports whether the document was adopted.
 */
async function adopt(deps: EndpointRefreshDeps, doc: unknown, via: string): Promise<boolean> {
  const result = await deps.engine.updateEndpointList(doc);
  if (!result.ok) {
    // unknown-signer / bad-signature / stale-version / malformed: all
    // expected outcomes for an untrusted channel, never an error log.
    say(deps, 'debug', `endpoint refresh: not adopted from ${via} (${result.reason})`);
    return false;
  }
  setEndpointPins(result.document as EndpointList);
  say(deps, 'info', `endpoint refresh: adopted v${result.document.version} from ${via}`);
  return true;
}

/**
 * Arm the recurring refresh timer (rides the polyfilled setTimeout, which
 * itself rides the host schedule/cancel seam — no drift-sensitive logic
 * here, the interval is simply re-armed after each attempt settles). Returns
 * a disposer; does NOT fire an initial fetch — callers fire that once,
 * separately, right after engine assembly (non-blocking boot fetch).
 */
export function scheduleEndpointRefresh(deps: EndpointRefreshDeps): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const armNext = (): void => {
    if (stopped) return;
    timer = setTimeout(tick, ENDPOINT_REFRESH_INTERVAL_MS);
  };
  const tick = (): void => {
    timer = null;
    void refreshEndpoints(deps).finally(armNext);
  };

  armNext();
  return () => {
    stopped = true;
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
}
